import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  TextField,
  Box,
  Pagination,
} from '@mui/material';
import { 
  fetchCustomersRequest,
  resetCustomerState 
} from '../../redux/Customers/actions/customerActions';
import { fetchCustomersByVendorRequest } from '../../redux/Vendors/actions/vendorActions';

import TextInputField from '../../component/ui/FormFields/TextInputField';
import { SearchIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { INSTANT_BOOKING_AIRPORT_DETAILS } from '../../component/common/Routes/AppRoutes';

const CustomerSelectionDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // State management
  const [search, setSearch] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [page, setPage] = useState(1);

  const rowsPerPage = 5;
  const searchTimerRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Redux selectors
  const user = useSelector((state) => state.auth.user);
  
  // Fixed selectors to get the correct loading and data states
  const {
    customers: response,
    loading,
    success,
    error,
  } = useSelector((state) =>
    user?.user_type === 2 ? state.vendors : state.customers
  );

  // Data extraction
  const customers = response?.data || [];
  const totalRecords = response?.totalRecords || 0;
  const totalPages = response?.totalPages || 1;

  // Fetch customers function
  const fetchCustomers = (
    pageNumber,
    searchQuery = search,
    searchBy = searchColumn,
    isPageChange = false
  ) => {
    const params = {
      pagination_required: true,
      pagesize: rowsPerPage,
      pageNo: pageNumber,
      status: 1, // Only fetch active customers for selection
    };

    if (searchQuery) {
      params.search = searchQuery;
      if (searchBy && searchBy.trim() !== "") {
        params.search_by = searchBy;
      }
    }


    if (user?.user_type === 2) {
      // Don't reset state immediately before fetching - this can cause race conditions
      dispatch(
        fetchCustomersByVendorRequest({
          ...params,
          vendor_id: user?.vendor_id,
        })
      );
    } else {
      dispatch(fetchCustomersRequest(params));
    }
  };

  // Handle search with debouncing
  const handleSearch = (query) => {
    setSearch(query);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchCustomers(1, query, searchColumn, false);
    }, 500);
  };

  // Handle page change
  const handleChangePage = (event, value) => {
    setPage(value);
  };

  // Initial load effect
  useEffect(() => {
    if (open && isInitialLoad.current) {
      dispatch(resetCustomerState());
      setSearch('');
      setSearchColumn('');
      setPage(1);
      
      // Add a small delay to ensure state is reset before fetching
      setTimeout(() => {
        fetchCustomers(1, '', '', false);
      }, 100);
      
      isInitialLoad.current = false;
    }
  }, [open]);

  // Page change effect
  useEffect(() => {
    if (open && !isInitialLoad.current) {
      fetchCustomers(page, search, searchColumn, true);
    }
  }, [page]);

  // Reset on dialog close
  useEffect(() => {
    if (!open) {
      isInitialLoad.current = true;
      setSearch('');
      setPage(1);
      dispatch(resetCustomerState());
    }
  }, [open, dispatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleCustomerSelect = (customer) => {
    navigate(INSTANT_BOOKING_AIRPORT_DETAILS, {
      state: {
        customerDetails: customer,
      },
    });
    onClose();
  };

  // Transform customers data for display
  const transformedCustomers = useMemo(() => {
   
    return customers.map((customer) => ({
      id: customer?.id,
      full_name:
        customer.full_name ||
        `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
      first_name: customer?.first_name,
      last_name: customer?.last_name,
      email: customer?.email,
      phone: customer?.phone,
      // Include other fields that might be needed for navigation
      ...customer
    }));
  }, [customers]);

  
  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Select Customer</DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: '10px', marginBottom: '20px' }}>
          <TextInputField
            fullWidth
            label={'Search Customers'}
            variant='outlined'
            placeholder={'Search Customer by name, email, or phone'}
            startAdornment={<SearchIcon />}
            size='small'
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Box ml={2}>Loading customers...</Box>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <div>Error loading customers: {error.message || 'Please try again.'}</div>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transformedCustomers.length > 0 ? (
                  transformedCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.full_name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>
                        <Button
                          sx={{ backgroundColor: '#ed6c02' }}
                          variant='contained'
                          size='small'
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align='center'>
                      {search ? 'No customers found matching your search.' : 'No customers available.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {transformedCustomers.length > 0 && (
              <Box mt={2} display='flex' justifyContent='center' alignItems='center'>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handleChangePage}
                  color='primary'
                  disabled={loading}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerSelectionDialog;